/* import __COLOCATED_TEMPLATE__ from './analytics-chart.hbs'; */
import Component from '@glimmer/component';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { inject as service } from '@ember/service';

import ChartDataLabels from 'chartjs-plugin-datalabels';

const COLOR_A = [169, 116, 255];
const COLOR_B = [48, 241, 199];

const _lerp = (a, b, amount) => a + amount * (b - a);

const _gradient = (r1, g1, b1, r2, g2, b2, amount) => {
  const r = Math.floor(_lerp(r1, r2, amount));
  const g = Math.floor(_lerp(g1, g2, amount));
  const b = Math.floor(_lerp(b1, b2, amount));

  return _colorStringRGB(r, g, b);
};

const _colorStringRGB = (r, g, b) => `rgb(${r}, ${g}, ${b})`;

const _identity = (x) => x;

/**
 * Simple plugin for adding up all the data in a column in a stacked bar graph.
 * Primarily for use in the 'dataLabels' plugin.
 *
 * Adapted from: https://github.com/chartjs/chartjs-plugin-datalabels/issues/16#issuecomment-348333846
 * @type {{id: string, beforeUpdate: function(*=)}}
 */
const StackSumPlugin = {
  id: 'stackSum',

  beforeUpdate(chart) {
    const { labels, datasets } = chart.data;

    chart.$stackSum = labels.map((_, idx) => {
      let lastIdx = -1;
      let sum = 0;

      datasets.forEach((dataset, datasetIndex) => {
        // We need the proper datasetIndex for this whole thing to work so we
        // filter this way instead of doing a datasets.filter()
        if (chart.isDatasetVisible(datasetIndex)) {
          const item = dataset.data[idx];

          if (item) {
            sum += item;
            // this should be the index of the topmost dataset present in the chart
            lastIdx = datasetIndex;
          }
        }
      });

      return {
        sum,
        lastIdx,
      };
    });
  },
};

class AnalyticsChartComponent extends Component {
  @service media;

  #labelsCache = createCache(() => {
    this.args.chartData;
    return new Set();
  });

  get labels() {
    return Object.values(this.args.chartLabels);
  }

  get datasets() {
    const { chartData, groupedChartGraphColors } = this.args;
    const sortedKeys = Object.keys(chartData).sort();

    return sortedKeys.map((key, idx) => {
      const data = chartData[key];
      const specifiedColor = groupedChartGraphColors?.[key];

      let color = specifiedColor
        ? _colorStringRGB(...specifiedColor)
        : _colorStringRGB(...COLOR_B);

      if (!specifiedColor && sortedKeys.length !== 1) {
        const gradient = idx / Math.max(1, sortedKeys.length - 1);
        color = _gradient(...COLOR_A, ...COLOR_B, gradient);
      }

      return {
        label: key,
        data: Object.values(data),
        backgroundColor: color,
        hoverBackgroundColor: color,
      };
    });
  }

  get options() {
    const { height, width } = this.args;

    const {
      scales,
      datalabelOptions: datalabels,
      legendOptions: legend,
      tooltipOptions: tooltip,
    } = this;

    const plugins = { legend, datalabels, tooltip };

    return {
      plugins,
      scales,
      animation: false,
      defaultFontFamily: "'Inter', sans-serif",
      layout: {
        padding: {
          top: 32,
        },
      },
      maintainAspectRatio: false,
      responsive: !(width && height),
    };
  }

  get scales() {
    return {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          source: 'labels',
          autoSkip: false,
        },
        title: {
          display: true,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
      },
    };
  }

  get sumFormatter() {
    const { sumFormatter } = this.args;

    return typeof sumFormatter === 'function' ? sumFormatter : _identity;
  }

  get plugins() {
    return [ChartDataLabels, StackSumPlugin];
  }

  get legendOptions() {
    const labelsCache = this.#labelsCache;

    const {
      showLegend: display = true,
      reverseLegend: reverse = true,
    } = this.args;

    return {
      display,
      reverse,
      position: 'bottom',

      onClick(e, item) {
        const soloedDatasets = getValue(labelsCache);

        const { chart } = this;
        const { datasets } = chart.data;
        const { datasetIndex } = item;

        if (soloedDatasets.has(datasetIndex)) {
          soloedDatasets.delete(datasetIndex);
        } else {
          soloedDatasets.add(datasetIndex);
        }

        if (soloedDatasets.size === datasets.length) {
          soloedDatasets.clear();
        }

        const showAll = soloedDatasets.size === 0;

        datasets.forEach((_dataset, idx) => {
          const meta = chart.getDatasetMeta(idx);

          if (showAll || soloedDatasets.has(idx)) {
            meta.hidden = null;
          } else {
            meta.hidden = true;
          }
        });

        chart.update('none');
      },
    };
  }

  get datalabelOptions() {
    const {
      datalabelAlign,
      datalabelAnchor,
      datalabelRotation,
      sumFormatter,
    } = this;

    return {
      anchor: datalabelAnchor,
      align: datalabelAlign,
      clamp: true,
      rotation: datalabelRotation,

      formatter(_, ctx) {
        return sumFormatter(ctx.chart.$stackSum[ctx.dataIndex].sum);
      },

      display(ctx) {
        return ctx.datasetIndex === ctx.chart.$stackSum[ctx.dataIndex].lastIdx;
      },
    };
  }

  get datalabelAlign() {
    return this.media.isMobile
      ? 'center'
      : 'end';
  }


  get datalabelAnchor() {
    return this.media.isMobile
      ? 'end'
      : 'start';
  }

  get datalabelRotation() {
    return this.media.isMobile
      ? -90
      : 0;
  }

  get tooltipOptions() {
    const { usesGroups = true } = this.args;
    const { sumFormatter } = this;

    return {
      displayColors: usesGroups,

      callbacks: {
        label(context) {
          const value = sumFormatter(context.parsed.y);
          return usesGroups ? `${context.dataset.label}: ${value}` : value;
        },

        title() {
          return '';
        },
      },
    };
  }
}

export default AnalyticsChartComponent;
